// The Match screen's large countdown (`match-hud` **D-2**). Pure: no store read, no clock —
// `clockRemainingTicks` is already on the wire (`waggle-proto` `MatchStateObs`) and both terms of
// the conversion are **wire-required** (`manifest.tickRateHz`, `manifest.speedMultiplier`), so
// this converts ticks to a string and nothing else. DESIGN §7: sim ticks are the time base, never
// a wall clock — this reads the wire's own pacing rather than sampling one.
//
// **The countdown is wall time, the tick rate is sim time** (WAG-107). Ticks advance at
// `tickRateHz * speedMultiplier` per wall second, so a deathmatch — 27,000 ticks of season arc at
// 15 Hz, paced at 2x — is 30 sim minutes and 15 minutes of a player's afternoon. The number on
// screen is a promise about when they can get up, so it is the second one. Every other consumer of
// `tickRateHz` (renderer interpolation, season progress, dance windows) converts to *sim* time and
// deliberately does not multiply.

/** The placeholder shown when no honest conversion exists (no wire tick rate/speed yet). */
export const COUNTDOWN_UNAVAILABLE = '--:--';

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * `MM:SS` of **wall time** remaining, from `clockRemainingTicks` at the manifest's own tick rate
 * and stream speed.
 *
 * Rounds **up**: a countdown that reads `00:00` while ticks remain would announce an end that
 * has not happened, so the last second is shown until it is actually spent. Clamped at zero —
 * the clock can be driven past the end (a terminal tick arrives after the last observation).
 *
 * `speedMultiplier` is `1.0` for every match that is not the pvp prototype's accelerated one; an
 * unusable value reads as the placeholder rather than falling back to 1x, on the same rule the
 * tick rate follows — a wrong time is worse than an absent one.
 */
export function formatCountdown(
  remainingTicks: number,
  tickRateHz: number,
  speedMultiplier: number,
): string {
  if (!Number.isFinite(tickRateHz) || tickRateHz <= 0) {
    return COUNTDOWN_UNAVAILABLE;
  }
  if (!Number.isFinite(speedMultiplier) || speedMultiplier <= 0) {
    return COUNTDOWN_UNAVAILABLE;
  }
  const seconds = Math.max(0, Math.ceil(remainingTicks / (tickRateHz * speedMultiplier)));
  return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
}

/**
 * `MM:SS` **elapsed** at a tick — match time, for a prompt card's timestamp and the statistics
 * chart's x axis.
 *
 * The deliberate counterpart to {@link formatCountdown}, and it rounds the other way: a countdown
 * rounds up so the last second is not announced as spent, while an elapsed reading floors, so an
 * event is stamped with the minute it happened *in* rather than the one it is approaching. Both
 * take the rate from `manifest.tickRateHz` (**wire-required**) and return the same placeholder
 * without one — a raw tick number is not a time a person can read, and inventing 15 Hz would be
 * the twelfth-literal mistake the fixtures' own note warns about.
 */
export function formatMatchTime(tick: number, tickRateHz: number | null): string {
  if (tickRateHz === null || !Number.isFinite(tickRateHz) || tickRateHz <= 0) {
    return COUNTDOWN_UNAVAILABLE;
  }
  const seconds = Math.max(0, Math.floor(tick / tickRateHz));
  return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
}
